import { useEffect, useRef, useState, type FormEvent, type TransitionEvent } from 'react';
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router';
import { useSearchStore } from '../store/search.js';
import { Button } from './Button.js';
import { cx } from './classes.js';
import { SearchIcon } from './Icons.js';

/**
 * The search layout (owner instruction 2026-09-22): `/` and `/search` both
 * render through this shell, so the search field is the SAME mounted element
 * on either screen — a centred hero on home (headline above, helper line
 * below), a compact field docked top-left on results. On submit from home
 * the cards fade out, the URL changes, the field morphs into the corner
 * (CSS transitions on the theme's ease/duration), and the results screen
 * brings its filters and count line up only once the dock transition has
 * completed (`transitionend` on the field wrapper, with a timeout fallback).
 * Under `prefers-reduced-motion: reduce` there is no choreography at all:
 * the URL changes at once and everything renders in place.
 *
 * The morph's hero/docked layout is derived from the URL (`useLocation`),
 * never duplicated; only animation state (`leaving`, `settled`) is local.
 * The query and filters stay in the Zustand search store; results are
 * server data and stay in TanStack Query (the Results screen).
 */
export interface SearchShellContext {
  /** True once the field's dock transition completed (or no morph ran). */
  settled: boolean;
  /** True while the home content fades out ahead of the navigation. */
  leaving: boolean;
}

/** Children rendered outside the shell (unit tests) get the settled layout. */
export function useSearchShell(): SearchShellContext {
  return (
    useOutletContext<SearchShellContext | null>() ?? { settled: true, leaving: false }
  );
}

/** If `transitionend` never arrives, the choreography continues anyway. */
export const DOCK_FALLBACK_MS = 350;
/** How long the home cards get to fade out before the URL changes. */
export const LEAVE_MS = 220;

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** The collapse used by everything that exists only in the hero layout. */
const COLLAPSE =
  'grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none';
const OPEN = 'grid-rows-[1fr] opacity-100';
const COLLAPSED = 'grid-rows-[0fr] opacity-0';

export function SearchShell() {
  const location = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const storedQ = useSearchStore((s) => s.q);
  const recent = useSearchStore((s) => s.recent);
  const setQ = useSearchStore((s) => s.setQ);
  const remember = useSearchStore((s) => s.remember);

  const docked = location.pathname === '/search';
  const urlQ = (params.get('q') ?? '').trim();

  const [draft, setDraft] = useState(docked ? urlQ : storedQ);
  const [leaving, setLeaving] = useState(false);
  // A deep link straight to /search settles immediately: no morph ran.
  const [settled, setSettled] = useState(docked);
  const prevDocked = useRef(docked);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // On results the URL is the source of truth: it fills the field and the
  // store (deep links and the home screen's recent-search links included).
  useEffect(() => {
    if (!docked) return;
    setDraft(urlQ);
    if (urlQ) {
      setQ(urlQ);
      remember(urlQ);
    }
  }, [docked, urlQ, setQ, remember]);

  // The one morph is home → results: filters wait for the dock transition
  // (transitionend below, this timeout as the fallback). Going back home
  // un-settles so the next dock runs the choreography again.
  useEffect(() => {
    if (docked === prevDocked.current) return;
    prevDocked.current = docked;
    setLeaving(false);
    if (!docked) {
      setSettled(false);
      return;
    }
    if (prefersReducedMotion()) {
      setSettled(true);
      return;
    }
    setSettled(false);
    const t = setTimeout(() => setSettled(true), DOCK_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [docked]);

  useEffect(
    () => () => {
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    },
    [],
  );

  const onDockEnd = (e: TransitionEvent<HTMLDivElement>) => {
    if (docked && e.target === e.currentTarget) setSettled(true);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = draft.trim();
    if (trimmed.length < 2) return;
    setQ(trimmed);
    remember(trimmed);
    const to = `/search?q=${encodeURIComponent(trimmed)}`;
    if (docked || prefersReducedMotion()) {
      navigate(to);
      return;
    }
    if (leaving) return; // a second submit while the cards fade out
    setLeaving(true);
    leaveTimer.current = setTimeout(() => navigate(to), LEAVE_MS);
  };

  return (
    <main
      className={cx(
        'flex flex-col px-12 pb-16 transition-[padding,gap] duration-300 ease-out motion-reduce:transition-none',
        docked ? 'gap-7 pt-10' : 'gap-14 pt-[120px]',
      )}
    >
      <div className="flex w-full">
        <div
          aria-hidden="true"
          className={cx(
            'transition-[flex-grow] duration-300 ease-out motion-reduce:transition-none',
            docked ? 'grow-0' : 'grow',
          )}
        />
        <div
          data-testid="search-field-wrapper"
          data-docked={docked ? 'true' : undefined}
          onTransitionEnd={onDockEnd}
          className={cx(
            'flex max-w-full flex-col transition-[width] duration-300 ease-out motion-reduce:transition-none',
            docked ? 'w-[520px]' : 'w-[760px]',
          )}
        >
          <div aria-hidden={docked} className={cx(COLLAPSE, docked ? COLLAPSED : OPEN)}>
            <div className="overflow-hidden">
              <h1 className="m-0 pb-[22px] text-[40px] font-semibold leading-[1.1] tracking-[-0.02em]">
                Who are you dealing with?
              </h1>
            </div>
          </div>
          <div className={cx(COLLAPSE, docked ? COLLAPSED : OPEN)}>
            <div className="overflow-hidden">
              <label
                htmlFor="q"
                className="block pb-2.5 text-[13px] font-medium text-mute"
              >
                Company name, TPIN or Dira ID
              </label>
            </div>
          </div>
          <form
            onSubmit={submit}
            className={cx(
              'flex items-center gap-3.5 border border-line bg-surface transition-all duration-300 ease-out motion-reduce:transition-none',
              docked
                ? 'h-12 rounded-[10px] px-4'
                : 'h-[60px] rounded-card py-0 pr-2 pl-5 shadow-search',
            )}
          >
            <SearchIcon className="text-mute" />
            <input
              id="q"
              data-testid="search-input"
              type="search"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
              autoComplete="off"
              aria-label="Search"
              placeholder={docked ? undefined : 'zambeef'}
              className={cx(
                'min-w-0 grow bg-transparent text-ink outline-none transition-[font-size] duration-300 ease-out placeholder:text-mute/60 motion-reduce:transition-none',
                docked ? 'text-[16px]' : 'text-[18px]',
              )}
            />
            {/* Collapses on dock but stays the form's default button, so Enter
                still submits from the docked field (inert would suppress it). */}
            <span
              aria-hidden={docked}
              className={cx(
                'flex overflow-hidden transition-all duration-300 ease-out motion-reduce:transition-none',
                docked ? 'max-w-0 opacity-0' : 'max-w-40 opacity-100',
              )}
            >
              <Button type="submit" variant="primary" tabIndex={docked ? -1 : undefined}>
                Search
              </Button>
            </span>
          </form>
          <div
            inert={docked || undefined}
            className={cx(COLLAPSE, docked ? COLLAPSED : OPEN)}
          >
            <div className="overflow-hidden">
              <div className="flex items-center justify-between pt-2.5 text-[13px] text-mute">
                <span>
                  Reads PACRA and ZRA live when Dira’s index has no answer. Results say
                  when they were read.
                </span>
                {recent.length ? (
                  <span className="flex gap-3">
                    {recent.slice(0, 3).map((r) => (
                      <Link
                        key={r}
                        to={`/search?q=${encodeURIComponent(r)}`}
                        className="hover:text-ink"
                      >
                        {r}
                      </Link>
                    ))}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <div aria-hidden="true" className="grow" />
      </div>
      <Outlet context={{ settled, leaving } satisfies SearchShellContext} />
    </main>
  );
}
